import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { getLogger } from 'app-components/utils/core';

import {
  appendReasoningDelta,
  appendResponse,
  appendStreamingDelta,
  clearReasoningMessage,
  clearStreamingMessage,
  closeChat,
  setConversationError,
  setConversationLoading,
  setReasoningMessage,
  setThreadTitle,
} from './aiChatActions';
import { attachChartsToHistoricalMessages } from './aiChatChartUtils';
import {
  createAIConversation,
  deleteAIConversation,
  deleteAllAIConversations,
  fetchConversationMessagesById,
  fetchInitialConversations,
  loadMoreMessages,
  loadNextConversationsPage,
} from './aiChatThunks';
import type {
  ChatOpenPayload,
  ConversationItem,
  ConversationSummary,
  ToolDefinition,
  ToolHandlers,
} from './aiChatTypes';
import { AI_CHAT_REDUCER_KEY, type AiChatState, DEFAULT_AGENT, mkConversationData } from './aiChatTypes';

const log = getLogger('flux/ai-chat/aiChatSlice');

const isSystemMessage = (item: ConversationItem): boolean => item.role?.toLowerCase() === 'system';

const filterSystemMessages = (items: ConversationItem[]): ConversationItem[] =>
  items.filter((item) => !isSystemMessage(item));

const removeConversationsForAgents = (state: AiChatState, deletedAgents: string[]): void => {
  const deletedAgentsSet = new Set(deletedAgents);
  const deletedConversationIds = state.conversationsList
    .filter((conversation) => deletedAgentsSet.has(conversation.agent))
    .map((conversation) => conversation.id);

  state.conversationsList = state.conversationsList.filter((conversation) => !deletedAgentsSet.has(conversation.agent));
  deletedConversationIds.forEach((conversationId) => {
    delete state.conversationsData[conversationId];
  });

  if (state.currentConversationId && deletedConversationIds.includes(state.currentConversationId)) {
    state.currentConversationId = null;
    state.minimizedPreview = null;
  }

  state.conversationsMeta = {
    page: -1,
    pageSize: state.conversationsMeta.pageSize,
    hasMore: false,
  };
  state.conversationsRequestGeneration += 1;
};

const initialState: AiChatState = {
  chatUIState: 'closed',
  chatOpenPayload: null,
  minimizedPreview: null,
  currentConversationId: null,

  conversationsData: {},

  context: {},
  platformSpecificTools: [] as ToolDefinition[],
  platformSpecificToolsHandlers: {},

  conversationsList: [],
  isLoadingConversations: false,
  isFetchingInitialConversations: false,
  conversationsMeta: {
    page: -1,
    pageSize: 20,
    hasMore: false,
  },
  conversationsRequestGeneration: 0,

  error: null,
};

const aiChatSlice = createSlice({
  name: AI_CHAT_REDUCER_KEY,
  initialState,
  reducers: {
    setPlatformSpecificToolsHandlers(state, action: PayloadAction<ToolHandlers>) {
      state.platformSpecificToolsHandlers = action.payload as ToolHandlers;
    },

    setPlatformSpecificTools(state, action: PayloadAction<ToolDefinition[]>) {
      state.platformSpecificTools = action.payload as ToolDefinition[];
    },

    openChat: {
      reducer(state, action: PayloadAction<ChatOpenPayload | null | undefined>) {
        state.chatUIState = 'open';
        state.minimizedPreview = null;
        if (typeof action.payload !== 'undefined') {
          state.chatOpenPayload = action.payload ?? null;
        }
      },
      prepare(payload?: ChatOpenPayload | null) {
        return { payload };
      },
    },
    clearChatOpenPayload(state) {
      state.chatOpenPayload = null;
    },

    minimizeChat(state, action: PayloadAction<string | undefined>) {
      if (state.chatUIState === 'closed') return;
      state.chatUIState = 'minimized';
      state.minimizedPreview = action.payload ?? null;
    },

    setCurrentConversationId(state, action: PayloadAction<string | null>) {
      state.currentConversationId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(closeChat, (state) => {
        state.chatUIState = 'closed';
        state.currentConversationId = null;
        state.minimizedPreview = null;
        state.chatOpenPayload = null;
      })

      .addCase(appendResponse, (state, action) => {
        const { conversationId, item, charts } = action.payload;
        if (isSystemMessage(item)) return;
        const conv = state.conversationsData[conversationId];
        if (!conv) return;

        if (charts?.length) {
          conv.messages.push({ ...item, content: { ...item.content, charts } });
        } else {
          conv.messages.push(item);
        }
      })

      .addCase(appendStreamingDelta, (state, action) => {
        const { conversationId, delta } = action.payload;
        const conv = state.conversationsData[conversationId];
        if (conv) {
          conv.streamingMessage = (conv.streamingMessage || '') + delta;
        }
      })

      .addCase(appendReasoningDelta, (state, action) => {
        const { conversationId, delta } = action.payload;
        const conv = state.conversationsData[conversationId];
        if (conv) {
          conv.reasoningMessage = (conv.reasoningMessage || '') + delta;
        }
      })

      .addCase(clearStreamingMessage, (state, action) => {
        const conversationId = action.payload;
        const conv = state.conversationsData[conversationId];
        if (conv) {
          conv.streamingMessage = '';
        }
      })

      .addCase(clearReasoningMessage, (state, action) => {
        const conversationId = action.payload;
        const conv = state.conversationsData[conversationId];
        if (conv) {
          conv.reasoningMessage = null;
        }
      })

      .addCase(setConversationError, (state, action) => {
        const { conversationId, error } = action.payload;
        const conv = state.conversationsData[conversationId];
        if (conv) {
          conv.error = error;
        }
      })

      .addCase(setConversationLoading, (state, action) => {
        const { conversationId, loading } = action.payload;
        const conv = state.conversationsData[conversationId];
        if (conv) {
          conv.isLoading = loading;
        }
      })

      .addCase(setReasoningMessage, (state, action) => {
        const { conversationId, message } = action.payload;
        const conv = state.conversationsData[conversationId];
        if (conv) {
          conv.reasoningMessage = message;
        }
      })

      .addCase(setThreadTitle, (state, action) => {
        const { conversationId, title } = action.payload;
        const conv = state.conversationsData[conversationId];
        if (conv) {
          conv.title = title;
        }
        const summary = state.conversationsList.find((c) => c.id === conversationId);
        if (summary) {
          summary.title = title;
        }
      })

      .addCase(createAIConversation.pending, (state) => {
        state.isLoadingConversations = true;
        state.error = null;
      })

      .addCase(createAIConversation.fulfilled, (state, action) => {
        const { id, agent } = action.payload;

        state.conversationsData[id] = mkConversationData(id, agent);

        const newSummary: ConversationSummary = {
          id,
          externalId: undefined,
          agent,
          createdAt: new Date().toISOString(),
        };
        state.conversationsList = [newSummary, ...state.conversationsList];
        state.isLoadingConversations = false;
      })

      .addCase(createAIConversation.rejected, (state) => {
        state.isLoadingConversations = false;
        state.error = 'Failed to create conversation';
      })

      .addCase(fetchInitialConversations.pending, (state) => {
        state.isLoadingConversations = true;
        state.isFetchingInitialConversations = true;
        state.error = null;
      })

      .addCase(fetchInitialConversations.fulfilled, (state, action) => {
        const { content, number, last, size, requestGeneration } = action.payload;
        if (requestGeneration !== state.conversationsRequestGeneration) return;
        state.conversationsList = content;
        state.conversationsMeta.page = number;
        state.conversationsMeta.pageSize = size;
        state.conversationsMeta.hasMore = !last;
        state.isLoadingConversations = false;
        state.isFetchingInitialConversations = false;
      })

      .addCase(fetchInitialConversations.rejected, (state, action) => {
        if (action.payload?.requestGeneration !== state.conversationsRequestGeneration) return;
        state.isLoadingConversations = false;
        state.isFetchingInitialConversations = false;
        state.error = action.payload?.message ?? 'Failed to get AI conversations';
      })

      .addCase(loadNextConversationsPage.pending, (state) => {
        state.isLoadingConversations = true;
        state.error = null;
      })

      .addCase(loadNextConversationsPage.fulfilled, (state, action) => {
        const { content, number, last, size, requestGeneration } = action.payload;
        if (requestGeneration !== state.conversationsRequestGeneration) return;

        if (number === 0) {
          state.conversationsList = content;
        } else {
          state.conversationsList = state.conversationsList.concat(content);
        }

        state.conversationsMeta.page = number;
        state.conversationsMeta.pageSize = size;
        state.conversationsMeta.hasMore = !last;
        state.isLoadingConversations = false;
        state.error = null;
      })

      .addCase(loadNextConversationsPage.rejected, (state, action) => {
        if (action.payload?.requestGeneration !== state.conversationsRequestGeneration) return;
        state.isLoadingConversations = false;
        state.error = action.payload?.message ?? 'Failed to get AI conversations';
      })

      .addCase(fetchConversationMessagesById.pending, (state) => {
        state.error = null;
      })

      .addCase(fetchConversationMessagesById.fulfilled, (state, action) => {
        const conversationId = action.meta.arg;
        const res = action.payload;
        const summary = state.conversationsList.find((c) => c.id === conversationId);
        const agent = summary?.agent ?? DEFAULT_AGENT;
        const visibleItems = filterSystemMessages(res.items);

        state.conversationsData[conversationId] = {
          conversationId,
          agent,
          title: summary?.title,
          messages: visibleItems,
          streamingMessage: '',
          isLoading: false,
          reasoningMessage: null,
          error: null,
          nextPageCursor: res.nextPage ?? null,
          hasMoreItems: !!res.nextPage,
          isLoadingMore: false,
        };

        log.debug('fetchConversationMessagesById: Messages loaded', {
          totalMessages: visibleItems.length,
          agent,
        });
      })

      .addCase(fetchConversationMessagesById.rejected, (state, action) => {
        state.error = action.payload ?? 'Failed to fetch conversation';
      })

      .addCase(deleteAIConversation.pending, (state) => {
        state.isLoadingConversations = true;
        state.error = null;
      })

      .addCase(loadMoreMessages.pending, (state, action) => {
        const conversationId = action.meta.arg;
        if (conversationId) {
          const conv = state.conversationsData[conversationId];
          if (conv) {
            conv.isLoadingMore = true;
          }
        }
        state.error = null;
      })

      .addCase(loadMoreMessages.fulfilled, (state, action) => {
        const { items, nextPage, conversationId } = action.payload;
        const visibleItems = filterSystemMessages(items);

        const conv = state.conversationsData[conversationId];
        if (conv) {
          conv.isLoadingMore = false;
          const previousCursor = conv.nextPageCursor ?? null;
          const isTerminalPage =
            items.length === 0 || nextPage == null || (previousCursor != null && nextPage === previousCursor);

          if (visibleItems.length > 0) {
            conv.messages = attachChartsToHistoricalMessages([...visibleItems, ...conv.messages]);
          }

          if (isTerminalPage) {
            conv.nextPageCursor = null;
            conv.hasMoreItems = false;
          } else {
            conv.nextPageCursor = nextPage;
            conv.hasMoreItems = true;
          }
        }
      })

      .addCase(loadMoreMessages.rejected, (state, action) => {
        const conversationId = action.meta.arg;
        if (conversationId) {
          const conv = state.conversationsData[conversationId];
          if (conv) {
            conv.isLoadingMore = false;
          }
        }
        state.error = action.payload ?? 'Something went wrong loading more messages';
      })

      .addCase(deleteAIConversation.fulfilled, (state, action) => {
        const deletedId = action.payload;

        state.conversationsList = state.conversationsList.filter((c) => c.id !== deletedId);
        delete state.conversationsData[deletedId];

        if (state.currentConversationId === deletedId) {
          state.currentConversationId = null;
          state.minimizedPreview = null;
        }

        state.isLoadingConversations = false;
        log.debug('deleteAIConversation: Conversation removed from state', { deletedId });
      })

      .addCase(deleteAIConversation.rejected, (state, action) => {
        state.isLoadingConversations = false;
        state.error = action.payload ?? 'Failed to delete conversation';
      })

      .addCase(deleteAllAIConversations.pending, (state) => {
        state.isLoadingConversations = true;
        state.error = null;
      })

      .addCase(deleteAllAIConversations.fulfilled, (state, action) => {
        removeConversationsForAgents(state, action.payload);
        state.isLoadingConversations = false;
        state.isFetchingInitialConversations = false;
        log.debug('deleteAllAIConversations: Conversations removed from state', { deletedAgents: action.payload });
      })

      .addCase(deleteAllAIConversations.rejected, (state, action) => {
        const deletedAgents = action.payload?.deletedAgents ?? [];
        if (deletedAgents.length > 0) {
          removeConversationsForAgents(state, deletedAgents);
        }
        state.isLoadingConversations = false;
        state.isFetchingInitialConversations = false;
        state.error = action.payload?.message ?? 'Failed to delete all conversations';
      });
  },
});

export const {
  setPlatformSpecificTools,
  setPlatformSpecificToolsHandlers,
  openChat,
  clearChatOpenPayload,
  minimizeChat,
  setCurrentConversationId,
} = aiChatSlice.actions;

export {
  aiChatSlice,
  appendReasoningDelta,
  appendResponse,
  appendStreamingDelta,
  clearReasoningMessage,
  clearStreamingMessage,
  closeChat,
  setConversationError,
  setConversationLoading,
  setReasoningMessage,
  setThreadTitle,
};
